// EXP1-S6 (docs/build-briefs/EXP1-S6-google-sheet-export.md): the pure half of the Google Sheet export. No DOM and no
// network, so a plain node:test file can import it. Everything the browser sends to Google, and every sentence the
// person reads when it does not work, is built here.

export const GOOGLE_IDENTITY_SCRIPT_URL = "https://accounts.google.com/gsi/client";
/** Files this app creates, nothing else in the person's Drive. */
export const GOOGLE_DRIVE_FILE_SCOPE = "https://www.googleapis.com/auth/drive.file";
export const DRIVE_UPLOAD_URL =
  "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink";
export const GOOGLE_SHEET_MIME_TYPE = "application/vnd.google-apps.spreadsheet";
export const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const GOOGLE_SECONDARY_LINE = "needs a Google account";

export const GOOGLE_MESSAGES = Object.freeze({
  notFinished: "Google sign-in did not finish. Nothing was exported.",
  blocked:
    "Your browser blocked the Google sign-in window. Allow pop-ups and try again. Nothing was exported.",
  unavailable: "Could not reach Google. Check your connection. Nothing was exported.",
  uploadFailed: "Could not create the Google Sheet. Nothing was saved to your Google Drive.",
  creating: "Creating your Google Sheet.",
  opened: "Opened in Google Sheets.",
  popupBlocked: "Your Google Sheet is ready, but your browser blocked the new tab.",
});

/** The multipart body Drive's files.create takes: the file's metadata, then the workbook bytes. Returns the boundary and
 * the body parts so the caller assembles one Blob. The name is the report's file name without an extension. */
export function buildDriveMultipart(
  name: string,
  workbook: Blob,
  boundary: string,
): { contentType: string; body: Blob } {
  const metadata = JSON.stringify({ name, mimeType: GOOGLE_SHEET_MIME_TYPE });
  const head =
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${metadata}\r\n` +
    `--${boundary}\r\nContent-Type: ${XLSX_MIME_TYPE}\r\n\r\n`;
  const tail = `\r\n--${boundary}--`;
  return {
    contentType: `multipart/related; boundary=${boundary}`,
    body: new Blob([head, workbook, tail]),
  };
}

export type TokenResponse = { access_token?: string; expires_in?: number | string; error?: string };

/** The token to hold, or null when Google answered with an error or no token. A token is treated as expired a minute
 * early so an upload never starts on one about to lapse. */
export function readTokenResponse(
  response: TokenResponse,
  nowMs: number,
): { value: string; expiresAtMs: number } | null {
  if (
    typeof response.access_token !== "string" ||
    response.access_token === "" ||
    response.error !== undefined
  ) {
    return null;
  }
  const seconds = Number(response.expires_in);
  const lifetimeMs = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 3600 * 1000;
  return { value: response.access_token, expiresAtMs: nowMs + lifetimeMs - 60_000 };
}

/** Why a token request failed, from the token client's error callback type. */
export function tokenFailureReason(type: string | undefined): "blocked" | "cancelled" {
  return type === "popup_failed_to_open" ? "blocked" : "cancelled";
}

export function parseDriveFile(body: unknown): { webViewLink: string } | null {
  if (typeof body !== "object" || body === null) return null;
  const link = (body as { webViewLink?: unknown }).webViewLink;
  if (typeof link !== "string") return null;
  try {
    const url = new URL(link);
    return url.protocol === "https:" ? { webViewLink: url.toString() } : null;
  } catch {
    return null;
  }
}
